import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useBeforeUnload, useUnmount } from 'react-use';
import { useRouter } from 'next/router';
import { useApolloClient } from '@apollo/client';
import { AuthenticationException, AuthorizationException } from '@vodafoneis/sjonvarpskjarni-js-lib';
import Player from './Player/Player';
import PlaybackStarting from './PlaybackStarting';
import PlaybackStartingState from './PlaybackStarting/PlaybackStartingState';
import { PlaybackContext } from '../contexts/PlaybackContext';
import { REDIRECT_DELAY } from '../config/constants';
import { UserContext } from '../contexts/UserContext';
import ChannelPlaylistItem from '../models/ChannelPlaylistItem';
import { PlaybackState } from './Player/PlaybackState';
import { getChannel } from '../api/channel';

const { PREPARING, ERROR } = PlaybackStartingState;

export const PlaybackComponent: React.FC = () => {
	const router = useRouter();
	const client = useApolloClient();
	const { movieId, seriesId, channelId } = router.query;
	const player = useRef(null);
	const lastPosition = useRef(-1);
	const { isLoggedIn, requestAuthentication } = useContext(UserContext);
	const { startPlaylist, playlistItem, prevPlaylistItem, entitlement, next, reset, initialPlaybackPosition, playbackStartingState, setPlaybackStartingState, isReady, exception } =
		useContext(PlaybackContext);

	const redirectUrl = useMemo(() => {
		if (movieId) {
			return `/movie/${movieId}`;
		}

		if (seriesId) {
			return `/series/${seriesId}`;
		}

		if (channelId) {
			return `/live/${channelId}`;
		}

		return null;
	}, [channelId, movieId, seriesId]);

	useEffect(() => {
		(async () => {
			if (channelId && !playlistItem && !prevPlaylistItem) {
				try {
					const channel = await getChannel(Number(channelId));

					if (channel) {
						startPlaylist([new ChannelPlaylistItem(channel)]);
					}
				} catch {}
			}
		})();
	}, [channelId, client, playlistItem, prevPlaylistItem, startPlaylist]);

	useBeforeUnload(true, 'Ertu viss um að þú viljir stöðva afspilun?');

	const onUnmount = useCallback(() => {
		reset();
	}, [reset]);

	useUnmount(onUnmount);

	useEffect(() => {
		if (!playlistItem && prevPlaylistItem) {
			// If there is no current playlist item but there is a previous playlist item we are finishing playback.
			router.back();
		} else if (!playlistItem && (movieId || seriesId)) {
			router.replace(redirectUrl);
		}
	}, [router, playlistItem, prevPlaylistItem, redirectUrl, movieId, seriesId]);

	useEffect(() => {
		if (exception instanceof AuthenticationException) {
			// Thrown if user is not logged in
			requestAuthentication(redirectUrl);
		} else if (exception instanceof AuthorizationException && !isLoggedIn) {
			requestAuthentication(redirectUrl);
		}
	}, [exception, isLoggedIn, redirectUrl, requestAuthentication]);

	const onPlaybackError = useCallback(() => {
		setPlaybackStartingState(ERROR);
	}, [setPlaybackStartingState]);

	const updatePosition = useCallback(
		async (position) => {
			if (position === lastPosition.current) return;

			lastPosition.current = position;

			if (playlistItem) {
				try {
					await playlistItem.savePosition(position);
				} catch (e) {}
			}
		},
		[playlistItem]
	);

	useEffect(() => {
		if (player.current && playbackStartingState === PREPARING && isReady) {
			player.current.play();
		}
	}, [isReady, playbackStartingState]);

	const onPlaybackStateChange = useCallback(
		async (playbackState) => {
			if (playbackState === PlaybackState.PLAYING) {
				setPlaybackStartingState(null);
			} else if (playbackState === PlaybackState.IDLE && playbackStartingState !== ERROR) {
				next();
			}
		},
		[next, playbackStartingState, setPlaybackStartingState]
	);

	const onTimeUpdate = useCallback(
		({ position }) => {
			updatePosition(position);
		},
		[updatePosition]
	);

	const onPositionChanged = useCallback(
		({ position }) => {
			updatePosition(position);
		},
		[updatePosition]
	);

	useEffect(() => {
		if (playbackStartingState === ERROR) {
			const timeout = setTimeout(() => {
				reset(); // Reset the playlist after redirecting to keep playlistItem while showing the error message.
			}, REDIRECT_DELAY);
			return () => clearTimeout(timeout);
		}

		return () => {};
	}, [router, playbackStartingState, reset]);

	const PlayerComponent = Player as any;

	return (
		<>
			<PlayerComponent
				ref={player}
				playlistItem={playlistItem}
				entitlement={entitlement}
				isReady={isReady}
				positionToPlayFrom={initialPlaybackPosition}
				onPlaybackStateChange={onPlaybackStateChange}
				onPlaybackError={onPlaybackError}
				onCloseRequested={reset}
				onTimeUpdate={onTimeUpdate}
				onPositionChanged={onPositionChanged}
				playbackStartingState={playbackStartingState}
			/>
			<PlaybackStarting />
		</>
	);
};
